import {
  IconAppearance,
  IconGlyph,
  IconSize
} from '@efast_public/fjd-component-library/build/src/components/Icon/Icon.types';

export interface FjdTabBarTab {
  active?: boolean;
  closable?: boolean;
  data?: any;
  id: string;
  label?: string;
  withStatus?: boolean;
  statusIntent?: string;
  statusLabel?: string;
  statusIcon?: string;
  iconAppearance?: IconAppearance;
  iconSize?: IconSize;
  icon?: IconGlyph;
  pinnable?: boolean;
  pinned?: boolean;
}

import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';
import { RoutePaths } from '../../pages/AppRoutes';

import { useElementNavigation } from './useElementNavigation';

import { AntragObj } from '../../api/models/Antrag/AntragsModel';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { tabStatusConfig } from '../../pages/Antragsuebersicht/AntragsuebersichtCustomizedCell';
import { activeContentTabLocalStorageKey } from '../../pages/Schreibtisch/SchreibtischPage';

export const tabsStateLocalStorageKey = 'beh-tabs-state';

export interface TabsState {
  tabs: Array<FjdTabBarTab>;
  selectedTab: number;
}

const loadFromLocalStorage = <T,>(key: string, defaultValue: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (error) {
    console.error(`Failed to load ${key} from localStorage:`, error);
    return defaultValue;
  }
};

const saveToLocalStorage = <T,>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error);
  }
};

const getInitialTabsState = (): TabsState => {
  const stored = loadFromLocalStorage<TabsState | null>(
    tabsStateLocalStorageKey,
    null
  );

  if (stored && stored.tabs && stored.tabs.length > 0) {
    return {
      tabs: stored.tabs,
      selectedTab: stored.selectedTab
    };
  }

  return {
    tabs: [
      {
        active: true,
        icon: 'help-outline',
        id: 'empty',
        label: `Los geht's`
      }
    ],
    selectedTab: 0
  };
};

const tabsStateAtom = atom<TabsState>({
  key: 'tabsStateAtom',
  default: getInitialTabsState()
});

function useTabs() {
  const [tabsState, setTabsState] = useRecoilState(tabsStateAtom);

  const { navigateToElement } = useElementNavigation();
  const navigate = useNavigate();

  const { tabs, selectedTab } = tabsState;

  // Sync to localStorage whenever state changes
  useEffect(() => {
    if (tabs.length > 0 && tabs[0].id !== 'empty') {
      saveToLocalStorage(tabsStateLocalStorageKey, tabsState);
    }
  }, [tabsState, tabs]);

  const activeTab = tabs[selectedTab];

  const empty =
    !tabs || tabs.length === 0 || (tabs.length === 1 && tabs[0].id === 'empty');

  const handleTabChange = useCallback(
    (index: number) => {
      setTabsState((prev) => ({ ...prev, selectedTab: index }));
    },
    [setTabsState]
  );

  const handleTabClose = useCallback(
    (index: number) => {
      setTabsState((prev) => {
        const newTabs = prev.tabs.filter((_, i) => i !== index);

        if (newTabs.length === 0) {
          navigate(`${RoutePaths.ANTRAEGE}`);
          return {
            tabs: [],
            selectedTab: 0
          };
        }

        let newSelectedTab = prev.selectedTab;

        if (index === prev.selectedTab) {
          // Closing the selected tab
          const isLastTab = index === prev.tabs.length - 1;
          newSelectedTab = isLastTab ? index - 1 : index;
          navigateToElement(newTabs[newSelectedTab].data.element);
        } else if (index < prev.selectedTab) {
          // Closing a tab before the selected one
          newSelectedTab = prev.selectedTab - 1;
        }

        return {
          tabs: newTabs,
          selectedTab: newSelectedTab
        };
      });
    },
    [navigate, navigateToElement, setTabsState]
  );

  const closeTab = useCallback(
    (id: string) => {
      const indexToClose = tabs.findIndex((tab) => tab.id === id);
      if (indexToClose >= 0) {
        handleTabClose(indexToClose);
      }
    },
    [handleTabClose, tabs]
  );

  const openTab = useCallback(
    (element: CaseDetailsModel | AntragObj, shouldNavigate: boolean = true) => {
      if (!element || !element.caseId) {
        return;
      }

      setTabsState((prev) => {
        const existingIndex = prev.tabs.findIndex(
          (tab) => tab.id === element.caseId
        );

        if (existingIndex >= 0) {
          if (shouldNavigate) {
            navigateToElement(element);
          }
          return {
            ...prev,
            selectedTab: existingIndex
          };
        }

        const type: 'case' = 'case';
        const statusConfig = tabStatusConfig(
          element.bearbeitungsstand,
          element.bescheidstatus
        );

        const newTab: FjdTabBarTab = {
          active: false,
          closable: true,
          data: { element, type },
          id: element.caseId,
          label: `${element.auftragsnummer} • ${element.name}`,
          withStatus: statusConfig?.withStatus,
          statusIntent: statusConfig?.statusIntent,
          statusLabel: statusConfig?.statusLabel,
          statusIcon: statusConfig?.statusIcon,
          iconAppearance: statusConfig?.iconAppearance,
          iconSize: statusConfig?.iconSize,
          icon: statusConfig?.glyph,
          pinnable: false,
          pinned: false
        };

        const filteredTabs = prev.tabs.filter((tab) => tab.id !== 'empty');
        const newTabs = [...filteredTabs, newTab];
        const newIndex = newTabs.length - 1;

        if (shouldNavigate) {
          navigateToElement(element);
        }

        return {
          tabs: newTabs,
          selectedTab: newIndex
        };
      });
    },
    [navigateToElement, setTabsState]
  );

  const closeAllTabs = useCallback(() => {
    setTabsState((prev) => {
      for (const tab of prev.tabs) {
        localStorage.removeItem(`${activeContentTabLocalStorageKey}${tab.id}`);
      }
      return {
        tabs: [],
        selectedTab: 0
      };
    });
    navigate(`${RoutePaths.ANTRAEGE}`);
  }, [navigate, setTabsState]);

  const updateTab = useCallback(
    (element: CaseDetailsModel | AntragObj) => {
      if (!element || !element.caseId) return;

      setTabsState((prev) => {
        const index = prev.tabs.findIndex((tab) => tab.id === element.caseId);
        if (index < 0) return prev;

        const statusConfig = tabStatusConfig(
          element.bearbeitungsstand,
          element.bescheidstatus
        );

        const updatedTab: FjdTabBarTab = {
          ...prev.tabs[index],
          label: `${element.auftragsnummer} • ${element.name}`,
          data: { ...prev.tabs[index].data, element },
          withStatus: statusConfig?.withStatus,
          statusIntent: statusConfig?.statusIntent,
          statusLabel: statusConfig?.statusLabel,
          statusIcon: statusConfig?.statusIcon,
          iconAppearance: statusConfig?.iconAppearance,
          iconSize: statusConfig?.iconSize,
          icon: statusConfig?.glyph ? statusConfig.glyph : undefined
        };

        const newTabs = [...prev.tabs];
        newTabs[index] = updatedTab;
        return { ...prev, tabs: newTabs };
      });
    },
    [setTabsState]
  );

  const setSelectedTab = useCallback(
    (index: number) => {
      setTabsState((prev) => ({ ...prev, selectedTab: index }));
    },
    [setTabsState]
  );

  return {
    activeTab,
    closeAllTabs,
    closeTab,
    empty,
    handleTabChange,
    handleTabClose,
    openTab,
    selectedTab,
    setSelectedTab,
    tabs,
    updateTab
  };
}

export default useTabs;
